import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ComponentRef,
  effect,
  ElementRef,
  EnvironmentInjector,
  inject,
  inputBinding,
  OnDestroy,
  OnInit,
  outputBinding,
  runInInjectionContext,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { ApiClient } from '@core/services/api-client';
import { JwtDecoderService } from '@core/services/jwt-decoder-service';
import { Button } from '@shared/components/button/button';
import { CodeEditor } from '@shared/components/code-editor/code-editor';
import { Select, SelectItem } from '@shared/components/select/select';
import { TestCaseCard } from '@shared/components/test-case-card/test-case-card';
import { LanguageBoilerplate } from '@shared/models/language-boilerplate';
import { Problem, ProblemBoilerplate } from '@shared/models/problem';
import { TestCase, TestCaseResult } from '@shared/models/test-case';
import Quill from 'quill';
import { v4 as uuidv4 } from 'uuid';
import { TestCaseItem } from '../test-case-item/test-case-item';

@Component({
  selector: 'app-create-page',
  imports: [Select, TestCaseItem, TestCaseCard, Button],
  templateUrl: './create-page.html',
})
export class CreatePage implements OnInit, AfterViewInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly jwtDecoder = inject(JwtDecoderService);
  private readonly apiClient = inject(ApiClient);
  private readonly injector = inject(EnvironmentInjector);

  private readonly problemId = uuidv4();
  private readonly token = localStorage.getItem('accessToken');
  private readonly userId = this.jwtDecoder.getUserId(this.token!);

  // Tab
  readonly selectedTab = signal<string>('initial-code');

  // Test case
  readonly testCases = signal<TestCase[]>([]);

  // Test case result
  readonly running = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly testCaseDetail = signal<TestCaseResult[]>([]);

  // Code editor
  readonly codeEditorLanguage = signal<string>('');
  readonly codeEditorLanguageId = signal<string>('');
  readonly CodeEditorInputCode = signal<string>('');
  readonly CodeEditorOutputCode = signal<string>('');
  private codeEditorRef: ComponentRef<CodeEditor> | null = null;
  readonly codeEditorContainer = viewChild('codeEditorOutlet', {
    read: ViewContainerRef,
  });

  // Selector [Difficulty]
  private readonly DIFFICULTY_OPTIONS: SelectItem[] = [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' },
  ];
  readonly selectDifficultyItems = signal<SelectItem[]>(
    this.DIFFICULTY_OPTIONS
  );
  readonly selectedDifficulty = signal<SelectItem>(this.DIFFICULTY_OPTIONS[0]);

  // Selector [Language]
  private readonly LANGUAGE_OPTIONS: SelectItem[] = [
    { value: '63', label: 'javascript' },
    { value: '74', label: 'typescript' },
    { value: '71', label: 'python' },
  ];
  readonly selectLanguageItems = signal<SelectItem[]>(this.LANGUAGE_OPTIONS);
  readonly selectedLanguage = signal<SelectItem>(this.LANGUAGE_OPTIONS[0]);

  // Quill
  private quill!: Quill;
  readonly quillEditorContainer = viewChild('quillEditorContainer', {
    read: ElementRef,
  });
  readonly quillContent = signal<string>('');

  // Form validation
  readonly problemNameValid = signal<boolean>(false);
  readonly isVerify = signal<boolean>(false);
  readonly isQuillContentValid = signal<boolean>(false);
  readonly isInitialCodeValid = signal<boolean>(false);
  private isRunningVerification = false;
  readonly durationValid = signal<boolean>(true);

  ngOnInit() {
    runInInjectionContext(this.injector, () => {
      effect(() => {
        this.codeEditorLanguage.set(this.selectedLanguage().label || '');
        this.codeEditorLanguageId.set(this.selectedLanguage().value || '');

        if (this.selectedTab() === 'verification-code') {
          this.CodeEditorInputCode.set(
            this.getVerifyCode(Number(this.codeEditorLanguageId()))
          );
        } else if (this.selectedTab() === 'initial-code') {
          this.CodeEditorInputCode.set(
            this.getBaseCode(Number(this.codeEditorLanguageId()))
          );
        }
        this.setUpCodeEditorComponent();

        // Validate initial code when language or tab changes
        this.validateInitialCode();
      });
    });
  }

  ngAfterViewInit() {
    const el = this.quillEditorContainer()?.nativeElement;
    if (el) {
      this.quill = new Quill(el, {
        theme: 'snow',
        modules: {
          toolbar: [
            // Basic formatting
            ['bold', 'italic', 'underline', 'strike'],
            // Font options
            [{ size: ['small', false, 'large', 'huge'] }],
            // Color options
            [{ color: [] }, { background: [] }],
            // Header levels
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            // Lists
            [{ list: 'ordered' }, { list: 'bullet' }],
            // Indent, Align
            [{ indent: '-1' }, { indent: '+1' }],
            [{ align: [] }],
            // Media
            ['link', 'image', 'video'],
            // Code, quote, clear
            ['blockquote', 'code-block'],
            ['clean'],
          ],
        },
        placeholder: 'Write something...',
        readOnly: false,
        bounds: el,
      });

      this.quill.on('text-change', () => {
        const htmlContent = this.quill.root.innerHTML;
        this.quillContent.set(htmlContent);
        this.validateQuillContent(htmlContent);
      });
    }
  }

  ngOnDestroy() {
    const existingBoiler = localStorage.getItem('languageBoiler');
    if (!existingBoiler) return;
    let parsedBoiler: Problem[] = [];
    try {
      parsedBoiler = JSON.parse(existingBoiler) as Problem[];
    } catch (error) {
      console.error('Failed to parse languageBoiler from localStorage:', error);
      return;
    }

    // ดึงเฉพาะ languageBoilerPlate ของ problemId ปัจจุบัน
    const currentProblemIndex = parsedBoiler.findIndex(
      p => p.problemId === this.problemId
    );

    if (currentProblemIndex === -1) {
      console.error(
        'No boilerplate found for current problemId:',
        this.problemId
      );
      return;
    }
    // ลบ boilerplate ออกจาก localStorage หลังบันทึกสำเร็จ
    parsedBoiler.splice(currentProblemIndex, 1);
    localStorage.setItem('languageBoiler', JSON.stringify(parsedBoiler));
  }

  // === Tab Management ===
  handleSelectTab(options: string) {
    this.selectedTab.set(options);

    // Destroy and recreate code editor when switching to code tabs
    if (options === 'initial-code' || options === 'verification-code') {
      this.destroyCodeEditor();
    }
  }

  // === Quill Management ===
  private validateQuillContent(htmlContent: string): void {
    // Check if content is empty or just the default empty state
    const isEmpty =
      !htmlContent ||
      htmlContent === '<p><br></p>' ||
      htmlContent === '<p></p>' ||
      this.quill.getText().trim() === '';

    this.isQuillContentValid.set(!isEmpty);
  }

  // === Code Editor Management ===
  private setUpCodeEditorComponent() {
    const container = this.codeEditorContainer();
    if (!container || this.codeEditorRef) return;

    if (
      this.selectedTab() !== 'initial-code' &&
      this.selectedTab() !== 'verification-code'
    ) {
      return;
    }

    this.codeEditorRef = container.createComponent(CodeEditor, {
      bindings: [
        inputBinding('language', this.codeEditorLanguage),
        inputBinding('codeInput', this.CodeEditorInputCode),
        outputBinding('codeOutput', this.CodeEditorOutputCode),
      ],
    });

    this.codeEditorRef.instance.codeOutput.subscribe((code: string) => {
      this.saveInitialCodeToLocalStorage(code);

      // Validate initial code when code changes
      if (this.selectedTab() === 'initial-code') {
        this.validateInitialCode();
      }

      // Reset verification status when verification code changes
      if (
        this.selectedTab() === 'verification-code' &&
        !this.isRunningVerification
      ) {
        this.resetVerificationStatus();
      }
    });
  }

  private destroyCodeEditor() {
    if (this.codeEditorRef) {
      this.codeEditorRef.destroy();
      this.codeEditorRef = null;
    }
  }

  private getCodeFromCodeEditor(): string {
    if (this.codeEditorRef) {
      this.codeEditorRef.instance.getCode();
    }
    return '';
  }

  // === Test Cases Management ===
  private nextId = 1;
  addTestCase() {
    const id = this.nextId++;
    this.testCases.update(cases => [
      ...cases,
      {
        id,
        input: '',
        expectedOutput: '',
        timeLimit: null,
        memoryLimit: null,
        isExpanded: false,
        isVisible: true,
      },
    ]);
    this.resetVerificationStatus();
  }

  updateTestCase(id: number, updated: Partial<TestCase>) {
    this.testCases.update(cases =>
      cases.map(tc => (tc.id === id ? { ...tc, ...updated } : tc))
    );
    this.resetVerificationStatus();
  }

  removeTestCase(id: number) {
    this.testCases.update(cases => cases.filter(tc => tc.id !== id));
    this.resetVerificationStatus();
  }

  // === Form Management ===
  async handleSave(event: Event): Promise<void> {
    event.preventDefault();

    if (!this.isFormValid()) return;

    const existingBoiler = localStorage.getItem('languageBoiler');
    if (!existingBoiler) return;

    let parsedBoiler: Problem[] = [];
    try {
      parsedBoiler = JSON.parse(existingBoiler) as Problem[];
    } catch (error) {
      console.error('Failed to parse languageBoiler from localStorage:', error);
      return;
    }

    // ดึงเฉพาะ languageBoilerPlate ของ problemId ปัจจุบัน
    const currentProblemIndex = parsedBoiler.findIndex(
      p => p.problemId === this.problemId
    );

    if (currentProblemIndex === -1) {
      // console.error(
      //   'No boilerplate found for current problemId:',
      //   this.problemId
      // );
      return;
    }

    const currentProblem = parsedBoiler[currentProblemIndex];

    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);

    const bodyTestCases = this.testCases().map(item => ({
      input: item.input,
      isVisible: item.isVisible,
      expectedOutput: item.expectedOutput,
      timeLimit: item.timeLimit,
      memoryLimit: item.memoryLimit,
    }));

    const body = {
      userId: this.userId,
      title: formData.get('problemName'),
      description: this.quillContent(),
      duration: Number(formData.get('problemDuration')),
      difficulty: this.selectedDifficulty()?.value ?? null,
      testCases: bodyTestCases,
      languageBoiler: currentProblem.languageBoilerplate, //ส่งเฉพาะของปัญหานี้
    };

    console.log('Payload to API:', body);

    try {
      await this.apiClient.createProblem(body);
      // ลบ boilerplate ออกจาก localStorage หลังบันทึกสำเร็จ
      parsedBoiler.splice(currentProblemIndex, 1);
      localStorage.setItem('languageBoiler', JSON.stringify(parsedBoiler));
      this.router.navigate(['/problems']);
    } catch (err: unknown) {
      if (err instanceof HttpErrorResponse) {
        this.handleApiError(err);
      }
    }
  }

  isFormValid(): boolean {
    return (
      this.isVerify() &&
      this.isQuillContentValid() &&
      this.isInitialCodeValid() &&
      this.testCases().length > 0 &&
      this.problemNameValid() &&
      this.durationValid()
    );
  }

  private resetVerificationStatus() {
    if (this.isRunningVerification) return;

    if (this.isVerify()) {
      this.isVerify.set(false);
      this.testCaseDetail.set([]);
      this.errorMessage.set(null);
    }
  }

  goToProblems() {
    this.router.navigate(['/problems']);
  }

  // === Initial Code Management ==
  private saveInitialCodeToLocalStorage(code: string) {
    const isVerify = this.selectedTab() === 'verification-code';
    const existingData = localStorage.getItem('languageBoiler');
    let problemBoilerplates: ProblemBoilerplate[] = [];

    try {
      if (existingData) {
        problemBoilerplates = JSON.parse(existingData) as ProblemBoilerplate[];
      }
    } catch (error) {
      console.error('Failed to parse problems from localStorage:', error);
    }

    // หา Problem ปัจจุบัน
    let currentProblem = problemBoilerplates.find(
      p => p.problemId === this.problemId
    );
    if (!currentProblem) {
      currentProblem = {
        problemId: this.problemId,
        languageBoilerplate: [],
      };
      problemBoilerplates.push(currentProblem);
    }

    const langId = Number(this.codeEditorLanguageId());
    const langName = this.codeEditorLanguage();

    const existingLangIndex = currentProblem.languageBoilerplate.findIndex(
      p => p.langId === langId
    );

    if (existingLangIndex >= 0) {
      if (isVerify) {
        currentProblem.languageBoilerplate[existingLangIndex].verifyCode = code;
      } else {
        currentProblem.languageBoilerplate[existingLangIndex].starterCode =
          code;
        //currentProblem.languageBoilerplate[existingLangIndex].verifyCode = code;
      }
    } else {
      const newEntry: LanguageBoilerplate = {
        langId,
        langName,
        starterCode: '',
        verifyCode: '',
      };
      if (isVerify) {
        newEntry.verifyCode = code;
      } else {
        newEntry.starterCode = code;
        //newEntry.verifyCode = code;
      }
      currentProblem.languageBoilerplate.push(newEntry);
    }

    localStorage.setItem('languageBoiler', JSON.stringify(problemBoilerplates));
  }

  private validateInitialCode() {
    const existingData = localStorage.getItem('languageBoiler');
    if (!existingData) {
      this.isInitialCodeValid.set(false);
      return;
    }

    try {
      const problemBoilerplates: ProblemBoilerplate[] =
        JSON.parse(existingData);
      const currentProblem = problemBoilerplates.find(
        p => p.problemId === this.problemId
      );

      if (!currentProblem) {
        this.isInitialCodeValid.set(false);
        return;
      }

      // Check if at least one language has initial code
      const atLeastOneLanguageHasCode = this.LANGUAGE_OPTIONS.some(lang => {
        const langId = Number(lang.value);
        const entry = currentProblem.languageBoilerplate.find(
          p => p.langId === langId
        );
        return entry && entry.starterCode && entry.starterCode.trim() !== '';
      });

      this.isInitialCodeValid.set(atLeastOneLanguageHasCode);
    } catch (error) {
      console.error('Failed to validate initial code:', error);
      this.isInitialCodeValid.set(false);
    }
  }

  private getBaseCode(langId: number): string {
    const existingData = localStorage.getItem('languageBoiler');
    if (!existingData) return '';

    try {
      const problemBoilerplates: ProblemBoilerplate[] =
        JSON.parse(existingData);
      const currentProblem = problemBoilerplates.find(
        p => p.problemId === this.problemId
      );
      if (!currentProblem) return '';

      const entry = currentProblem.languageBoilerplate.find(
        p => p.langId === langId
      );
      return entry?.starterCode ?? '';
    } catch (error) {
      console.error('Failed to parse languageBoiler from localStorage:', error);
      return '';
    }
  }

  // === Verify Code Management ===
  async handleRunVerify(): Promise<void> {
    if (this.running()) return;

    this.isRunningVerification = true;
    this.prepareCodeExecution();

    const verifyTestCases = this.testCases().map(item => ({
      input: item.input,
      isVisible: item.isVisible,
      expectedOutput: item.expectedOutput,
      timeLimit: item.timeLimit ? item.timeLimit / 1000 : null,
      memoryLimit: item.memoryLimit ? item.memoryLimit : null,
    }));

    const editor = this.codeEditorRef?.instance;
    const currentCode = editor ? editor.getCode() : this.CodeEditorOutputCode();

    const body = {
      langId: Number(this.codeEditorLanguageId()),
      code: currentCode,
      testCases: verifyTestCases,
    };

    try {
      const result = await this.apiClient.verifyProblem(body);

      this.testCaseDetail.set(result);
      this.errorMessage.set(null);

      // Check if all test cases passed
      const allPassed = result.every((r: TestCaseResult) => r.passed === true);
      this.isVerify.set(allPassed);
    } catch (err: unknown) {
      if (err instanceof HttpErrorResponse) {
        this.handleApiError(err);
      }
    } finally {
      this.running.set(false);
      this.isRunningVerification = false;
    }
  }

  private prepareCodeExecution() {
    this.getCodeFromCodeEditor();
    this.running.set(true);
    this.errorMessage.set(null);
  }

  private getVerifyCode(langId: number): string {
    const existingData = localStorage.getItem('languageBoiler');
    if (!existingData) return '';

    try {
      const problemBoilerplates: ProblemBoilerplate[] =
        JSON.parse(existingData);
      const currentProblem = problemBoilerplates.find(
        p => p.problemId === this.problemId
      );
      if (!currentProblem) return '';

      const entry = currentProblem.languageBoilerplate.find(
        p => p.langId === langId
      );
      return entry?.verifyCode ?? '';
    } catch (error) {
      console.error('Failed to parse languageBoiler from localStorage:', error);
      return '';
    }
  }

  private handleApiError(err: HttpErrorResponse) {
    console.error(`Verification failed`, err);

    const map: Record<number, string> = {
      0: 'Cannot connect to the server.',
      400: 'Invalid request. Please check your input.',
      401: 'Unauthorized access',
      422: 'There is an error in your code. Please review and fix it.',
      500: 'Internal server error. Please check your code or try again.',
    };

    const msg =
      map[err.status] ||
      `An error occurred (${err.status}): ${err.error?.message || 'Please try again.'}`;
    this.errorMessage.set(msg);
    this.testCaseDetail.set([]);
  }
  validateProblemName(event: Event) {
    const target = event.target as HTMLInputElement | null;
    const value =
      target && typeof target.value === 'string' ? target.value.trim() : '';
    this.problemNameValid.set(!!value);
  }

  validateProblemDuration(event: Event) {
    const target = event.target as HTMLInputElement | null;
    const value =
      target && typeof target.value === 'string' ? target.value.trim() : '';
    this.durationValid.set(!!value && Number(value) > 0);
  }
}
